import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api, { fileUrl } from "@/lib/api";
import { Plus, Trash2, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const field = "w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:border-primary/50";
const lbl = "mb-1 block text-xs uppercase tracking-widest text-muted-foreground";

export default function AdminCreators() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", title: "", bio: "", avatar_url: "", cover_url: "" });

  const { data, isLoading } = useQuery({ queryKey: ["admin-creators"], queryFn: async () => (await api.get("/admin/creators")).data });

  const upImg = async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setF((s) => ({ ...s, [key]: data.url }));
    toast.success("Image téléversée");
  };

  const add = async () => {
    if (!f.name) return toast.error("Nom requis");
    await api.post("/admin/creators", {
      name: f.name, title: { fr: f.title, en: "" }, bio: { fr: f.bio, en: "" },
      avatar_url: f.avatar_url, cover_url: f.cover_url,
    });
    setF({ name: "", title: "", bio: "", avatar_url: "", cover_url: "" });
    toast.success("Créateur ajouté");
    qc.invalidateQueries({ queryKey: ["admin-creators"] });
  };
  const del = async (id) => { await api.delete(`/admin/creators/${id}`); qc.invalidateQueries({ queryKey: ["admin-creators"] }); };

  if (isLoading) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="max-w-4xl">
      <h1 className="font-heading text-2xl font-bold tracking-tight">{t("admin.creators")}</h1>

      <div className="mt-6 grid grid-cols-1 gap-3 rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-4 md:grid-cols-2">
        <div><label className={lbl}>Nom</label><input className={field} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="creator-name" /></div>
        <div><label className={lbl}>Titre / Rôle (FR)</label><input className={field} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="creator-title" /></div>
        <div className="md:col-span-2"><label className={lbl}>Bio (FR)</label><textarea rows={2} className={field} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="creator-bio" /></div>
        <div>
          <label className={lbl}>Avatar</label>
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5">
              {f.avatar_url ? <img src={fileUrl(f.avatar_url)} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-5 w-5 text-muted-foreground" />}
            </div>
            <label className="cursor-pointer rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10">Téléverser<input type="file" accept="image/*" className="hidden" onChange={(e) => upImg(e, "avatar_url")} /></label>
          </div>
        </div>
        <div>
          <label className={lbl}>Bannière</label>
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-24 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/5">
              {f.cover_url ? <img src={fileUrl(f.cover_url)} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-5 w-5 text-muted-foreground" />}
            </div>
            <label className="cursor-pointer rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs hover:bg-white/10">Téléverser<input type="file" accept="image/*" className="hidden" onChange={(e) => upImg(e, "cover_url")} /></label>
          </div>
        </div>
        <div className="md:col-span-2">
          <button onClick={add} data-testid="creator-add" className="flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-[hsl(var(--primary))]/90"><Plus className="h-4 w-4" /> Ajouter</button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        {data.map((c) => (
          <div key={c.id} className="flex items-center gap-3 rounded-xl border border-white/5 bg-[hsl(var(--surface-elevated))] p-3" data-testid={`creator-row-${c.id}`}>
            <img src={fileUrl(c.avatar_url)} alt="" className="h-12 w-12 rounded-full object-cover" />
            <div className="flex-1">
              <p className="font-medium">{c.name}</p>
              <p className="text-xs text-muted-foreground">{c.title?.fr}</p>
            </div>
            <button onClick={() => del(c.id)} className="rounded-md bg-white/5 p-2 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
